/* statePlateAddress.js — dirección con la que se cotiza el seguro según la
 * placa (estado de emplacamiento).
 *
 * Salió de Hooks/useCarCalculator.js para que la usen igual el cotizador
 * (/cotizar, admin) y la calculadora de la landing. Orden:
 *   1. la dirección default de la placa en BD (Admin → Dirección por placas);
 *   2. el respaldo fijo por nombre de estado;
 *   3. null (quien llama decide: la landing cae a CDMX).
 *
 * El catálogo de placas llega de GET api/getStaticData (`state`).
 */

export function normalizeAddressValue(value) {
    if (value === null || value === undefined) {
        return null;
    }

    const normalized = value.toString().trim().toUpperCase();
    return normalized === '' ? null : normalized;
}

const CDMX_FALLBACK = {
    zip: '03710',
    colonia: 'CIUDAD DE LOS DEPORTES',
    municipio: 'BENITO JUAREZ',
    ciudad: 'CIUDAD DE MEXICO',
    estado: 'CIUDAD DE MEXICO',
};

const EDOMEX_FALLBACK = {
    zip: '55090',
    colonia: 'EL POTRERO',
    municipio: 'ECATEPEC DE MORELOS',
    ciudad: 'ECATEPEC DE MORELOS',
    estado: 'ESTADO DE MEXICO',
};

// Morelos no tiene respaldo propio: sin dirección en BD se cotiza con la de
// CDMX, como siempre ha hecho /cotizar.
export const STATE_PLATE_ADDRESS_FALLBACKS = {
    'CIUDAD DE MEXICO': CDMX_FALLBACK,
    'CDMX': CDMX_FALLBACK,
    'ESTADO DE MEXICO': EDOMEX_FALLBACK,
    'EDOMEX': EDOMEX_FALLBACK,
    'MORELOS': CDMX_FALLBACK,
};

export function hasStatePlateDefaultAddress(statePlate) {
    return Boolean(
        statePlate?.default_zip &&
        statePlate?.default_colony &&
        statePlate?.default_municipality &&
        statePlate?.default_city &&
        statePlate?.default_state
    );
}

export function getStatePlateAddressDefaults(statePlate) {
    if (!hasStatePlateDefaultAddress(statePlate)) {
        return null;
    }

    return {
        zip: statePlate.default_zip?.toString().trim() || null,
        colonia: normalizeAddressValue(statePlate.default_colony),
        municipio: normalizeAddressValue(statePlate.default_municipality),
        ciudad: normalizeAddressValue(statePlate.default_city),
        estado: normalizeAddressValue(statePlate.default_state),
    };
}

/* { zip, colonia, municipio, ciudad, estado } | null */
export function getAddressForStatePlate(statePlate) {
    const dbDefaults = getStatePlateAddressDefaults(statePlate);
    if (dbDefaults) {
        return dbDefaults;
    }

    const stateName = normalizeAddressValue(statePlate?.state);
    if (stateName && STATE_PLATE_ADDRESS_FALLBACKS[stateName]) {
        return { ...STATE_PLATE_ADDRESS_FALLBACKS[stateName] };
    }

    return null;
}

const LABELS = {
    'CDMX': 'CDMX',
    'CIUDAD DE MEXICO': 'CDMX',
    'EDOMEX': 'Edo. Méx.',
    'ESTADO DE MEXICO': 'Edo. Méx.',
    'MORELOS': 'Morelos',
};

const titleCase = (s) => s.toLowerCase().replace(/(^|\s)\S/g, (c) => c.toUpperCase());

/* Etiqueta corta para el selector de placas: CDMX · Edo. Méx. · Morelos */
export function labelPlaca(statePlate) {
    const name = normalizeAddressValue(statePlate?.state);
    if (!name) return '';
    return LABELS[name] || titleCase(name);
}
